import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Loader2, Printer, Download, ArrowLeft, MapPin, Phone, Clock, Globe, Languages, Smartphone } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES, getCategoryLabel, t, isRTL } from '../lib/i18n';
import { DirectionsButton } from '../components/DirectionsButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PUBLIC_URL = process.env.REACT_APP_BACKEND_URL;

const PrintableHandout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ resources: [], settings: {} });
  const [currentLanguage, setCurrentLanguage] = useState(searchParams.get('lang') || 'en');
  const [translatedResources, setTranslatedResources] = useState({});
  const [translating, setTranslating] = useState(false);
  
  const categories = searchParams.get('categories') || '';
  const neighborhood = searchParams.get('neighborhood') || '';
  const cost = searchParams.get('cost') || '';
  const ids = searchParams.get('ids') || '';
  const shareUrl = `${PUBLIC_URL}/share?${searchParams.toString()}`;

  const translateResources = useCallback(async (lang, resourceList) => {
    if (lang === 'en' || resourceList.length === 0) {
      setTranslatedResources({});
      return;
    }
    setTranslating(true);
    try {
      const textsToTranslate = resourceList.map(r => ({
        id: r.id, resource_name: r.resource_name || '',
        client_facing_description: r.client_facing_description || '',
        eligibility_requirements: r.eligibility_requirements || ''
      }));
      const response = await axios.post(`${API_URL}/translate`, {
        texts: textsToTranslate, target_language: lang
      }, { timeout: 120000 });
      const translationMap = {};
      response.data.translations.forEach(t => { translationMap[t.id] = t; });
      setTranslatedResources(translationMap);
      toast.success(`Translated to ${SUPPORTED_LANGUAGES[lang]}`);
    } catch {
      toast.error('Translation failed. Please try again.');
    } finally {
      setTranslating(false);
    }
  }, []);

  // Client-facing document: always render in light theme, ignoring staff dark mode.
  useEffect(() => {
    const hadDark = document.documentElement.classList.contains('dark');
    document.documentElement.classList.remove('dark');
    return () => { if (hadDark) document.documentElement.classList.add('dark'); };
  }, []);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const params = new URLSearchParams();
        if (categories) params.append('sdoh_categories', categories);
        if (neighborhood) params.append('neighborhood', neighborhood);
        if (cost) params.append('cost', cost);
        if (ids) params.append('ids', ids);
        
        const response = await axios.get(`${API_URL}/public/resources?${params.toString()}`);
        setData(response.data);
        
        const initialLang = searchParams.get('lang');
        if (initialLang && initialLang !== 'en' && response.data.resources.length > 0) {
          translateResources(initialLang, response.data.resources);
        }
      } catch (error) {
        console.error('Failed to load resources:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [categories, neighborhood, cost, ids, searchParams, translateResources]);

  const handleLanguageChange = useCallback((lang) => {
    setCurrentLanguage(lang);
    translateResources(lang, data.resources);
  }, [translateResources, data.resources]);

  const getDisplayText = useCallback((resource, field) => {
    if (currentLanguage === 'en' || !translatedResources[resource.id]) return resource[field] || '';
    return translatedResources[resource.id][field] || resource[field] || '';
  }, [currentLanguage, translatedResources]);

  const getResourceName = useCallback((resource) => {
    const originalName = resource.resource_name || '';
    if (currentLanguage === 'en' || !translatedResources[resource.id]) return originalName;
    return translatedResources[resource.id].resource_name || originalName;
  }, [currentLanguage, translatedResources]);

  const groupedResources = useMemo(() => data.resources.reduce((acc, resource) => {
    const cat = resource.primary_sdoh_category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(resource);
    return acc;
  }, {}), [data.resources]);

  const filterSummary = useMemo(() => [
    categories && `${categories.split(',').length} categories`,
    neighborhood, cost
  ].filter(Boolean).join(' • '), [categories, neighborhood, cost]);

  const handlePrint = useCallback(() => { window.print(); }, []);

  const handleDownloadPDF = useCallback(() => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    const margin = 15;
    const lineHeight = 6;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(data.settings?.header_title || 'Community Resources', margin, y);
    y += 12;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    const filterText = [
      categories && `Categories: ${categories}`,
      neighborhood && `Neighborhood: ${neighborhood}`,
      cost && `Cost: ${cost}`
    ].filter(Boolean).join(' | ');
    if (filterText) { doc.text(filterText, margin, y); y += 8; }
    doc.setTextColor(0);
    
    Object.entries(groupedResources).forEach(([category, resources]) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(92, 64, 35);
      doc.rect(margin, y - 5, pageWidth - margin * 2, 8, 'F');
      doc.setTextColor(255);
      doc.text(category, margin + 3, y);
      doc.setTextColor(0);
      y += 10;
      
      resources.forEach((r) => {
        if (y > 260) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(r.resource_name || '', margin, y);
        y += lineHeight;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(r.organization || '', margin, y);
        y += lineHeight;
        const contactInfo = [r.phone_number && `Tel: ${r.phone_number}`, r.physical_address].filter(Boolean).join(' | ');
        if (contactInfo) { doc.text(contactInfo, margin, y); y += lineHeight; }
        if (r.hours) { doc.text(`Hours: ${r.hours}`, margin, y); y += lineHeight; }
        if (r.client_facing_description) {
          const descLines = doc.splitTextToSize(r.client_facing_description, pageWidth - margin * 2);
          doc.text(descLines.slice(0, 3), margin, y);
          y += Math.min(descLines.length, 3) * lineHeight;
        }
        y += 4;
      });
      y += 6;
    });
    
    if (data.settings?.footer_text) {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(data.settings.footer_text, margin, doc.internal.pageSize.getHeight() - 10);
    }
    doc.save('resources.pdf');
  }, [data.settings, groupedResources, categories, neighborhood, cost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#5C4023]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="no-print sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
              <Download className="w-4 h-4 mr-2" />PDF
            </Button>
            <Button size="sm" onClick={handlePrint} className="bg-[#5C4023] hover:bg-[#4A331C]">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 print:px-0 print:py-4" dir={isRTL(currentLanguage) ? 'rtl' : 'ltr'}>
        <div className="mb-5 print:mb-3">
          <h1 className="text-2xl font-bold text-[#1A1A1A] font-['Manrope'] print:text-xl">
            {data.settings?.header_title || t('listTitle', currentLanguage)}
          </h1>
          {filterSummary && <p className="text-sm text-gray-500 mt-1">{filterSummary}</p>}
        </div>

        {/* QR banner — 617Reach branded, high contrast, immune to dark mode / inverted themes */}
        <div className="relative overflow-hidden flex items-center gap-5 rounded-2xl bg-[#E8DFD3] p-4 mb-6 print:mb-4 print:rounded-lg print:border print:border-[#C87D3E]" style={{ colorScheme: 'light' }}>
          <span aria-hidden="true" className="absolute -right-3 -top-12 text-[130px] leading-none font-black text-[#5C4023]/10 -rotate-12 pointer-events-none select-none font-['Manrope']">617/</span>
          <div className="relative bg-white p-2.5 rounded-lg border-4 border-[#C87D3E] shrink-0" style={{ colorScheme: 'light', filter: 'none' }}>
            <QRCodeSVG value={shareUrl} size={112} level="M" bgColor="#FFFFFF" fgColor="#291D12"
              marginSize={2} className="print:w-24 print:h-24 block" />
          </div>
          <div className="relative flex-1">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#B3771E] font-semibold mb-1 print:text-[9px]">Resources In Reach</p>
            <div className="flex items-center gap-2 text-[#5C4023] font-semibold mb-1">
              <Smartphone className="w-5 h-5 shrink-0" />
              <span className="text-base print:text-sm font-['Manrope']">{t('scanTitle', currentLanguage)}</span>
            </div>
            <p className="text-sm text-[#3A2A1A] print:text-xs leading-relaxed">{t('scan', currentLanguage)}</p>
          </div>
        </div>

        <Separator className="mb-6 print:mb-4" />

        {data.resources.length === 0 && (
          <div className="text-center py-12"><p className="text-gray-500">{t('noResources', currentLanguage)}</p></div>
        )}

        {Object.entries(groupedResources).map(([category, resources]) => (
          <div key={category} className="mb-8 print:mb-4 print:break-inside-avoid">
            <div className="bg-[#5C4023] text-white px-4 py-2 rounded-lg mb-4 print:rounded-none print:mb-2">
              <h2 className="text-lg font-semibold font-['Manrope'] print:text-base">{getCategoryLabel(category, currentLanguage)}</h2>
              <span className="text-sm text-white/80">{resources.length} {resources.length !== 1 ? t('resources', currentLanguage) : t('resource', currentLanguage)}</span>
            </div>
            <div className="space-y-4 print:space-y-2">
              {resources.map((resource) => (
                <Card key={resource.id} className="border border-gray-200 print:border-gray-300 print:shadow-none">
                  <CardContent className="p-4 print:p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#1A1A1A] print:text-sm">{getResourceName(resource)}</h3>
                        <p className="text-sm text-gray-600 print:text-xs">{resource.organization}</p>
                      </div>
                      {resource.cost_to_client === 'Free' && (
                        <span className="text-xs bg-[#FDF4E3] text-[#B3771E] px-2 py-0.5 rounded-full print:bg-gray-100 print:text-gray-800">
                          {t('free', currentLanguage)}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm print:text-xs">
                      {resource.physical_address && (
                        <div className="flex items-start text-gray-600">
                          <MapPin className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 print:w-3 print:h-3" /><span>{resource.physical_address}</span>
                        </div>
                      )}
                      {resource.phone_number && (
                        <div className="flex items-center text-gray-600">
                          <Phone className="w-4 h-4 mr-2 flex-shrink-0 print:w-3 print:h-3" /><span>{resource.phone_number}</span>
                        </div>
                      )}
                      {resource.hours && (
                        <div className="flex items-center text-gray-600">
                          <Clock className="w-4 h-4 mr-2 flex-shrink-0 print:w-3 print:h-3" /><span>{resource.hours}</span>
                        </div>
                      )}
                      {resource.website && (
                        <div className="flex items-center text-gray-600 no-print">
                          <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
                          <a href={resource.website} target="_blank" rel="noopener noreferrer" className="text-[#5C4023] hover:underline truncate">
                            {resource.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                      <div className="no-print pt-1">
                        <DirectionsButton address={resource.physical_address} lang={currentLanguage} resourceId={resource.id} />
                      </div>
                    </div>
                    {resource.client_facing_description && (
                      <p className="mt-3 text-sm text-gray-700 print:text-xs print:mt-2">
                        {getDisplayText(resource, 'client_facing_description')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {data.settings?.footer_text && (
          <div className="mt-8 pt-4 border-t-2 border-[#5C4023] print:border-t">
            <div className="bg-[#F3EFE9] rounded-lg p-4 print:bg-gray-50 print:p-3">
              <p className="text-sm text-[#1A1A1A] whitespace-pre-line print:text-xs">{data.settings.footer_text}</p>
            </div>
          </div>
        )}
      </div>

      <div className="no-print fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Languages className="w-4 h-4" /><span>{t('translate', currentLanguage)}</span>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
              <Button key={code} variant={currentLanguage === code ? "default" : "outline"} size="sm"
                onClick={() => handleLanguageChange(code)} disabled={translating} data-testid={`print-lang-${code}`}
                className={`rounded-full text-xs whitespace-nowrap ${
                  currentLanguage === code ? 'bg-[#5C4023] hover:bg-[#4A331C] text-white' : 'border-gray-300 hover:border-[#5C4023] hover:text-[#5C4023]'
                }`}>{name}</Button>
            ))}
            {translating && <Loader2 className="w-4 h-4 animate-spin text-[#5C4023] ml-2" />}
          </div>
        </div>
      </div>
      <div className="h-16 no-print" />
      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PrintableHandout;
